import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { FaPlus, FaEdit, FaTrash, FaStar, FaCheck, FaTimes } from 'react-icons/fa';
import api from '../services/api';
import { normalizeListForAdmin, toBool } from '../utils/normalizeApi';

function TestimonialsManagement() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    role: 'Parent',
    testimonial: '',
    rating: 5,
    featured: false,
    isApproved: true, // Auto-approve new testimonials
    className: '',
    imageUrl: '',
  });

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await api.get('/testimonials/all');
      console.log('⭐ Fetched testimonials:', response.data);
      setTestimonials(normalizeListForAdmin(response.data));
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      setTestimonials([
        { id: 1, name: 'Sunita Devi', role: 'Parent', testimonial: 'Excellent school!', rating: 5, featured: true, isApproved: true }
      ]);
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/testimonials/${editingId}`, {
          ...formData,
          imageUrl: formData.imageUrl || '',
          isActive: true,
        });
        alert('✅ Testimonial updated successfully!');
      } else {
        await api.post('/testimonials/admin', {
          ...formData,
          imageUrl: formData.imageUrl || '',
          isActive: true,
        });
        alert('✅ Testimonial added successfully!');
      }
      setShowForm(false); setEditingId(null); resetForm(); fetchTestimonials();
    } catch (error) {
      console.error('CRUD Error:', error);
      alert('❌ Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name,
      role: item.role || 'Parent',
      testimonial: item.testimonial,
      rating: item.rating || 5,
      featured: toBool(item.featured, false),
      isApproved: toBool(item.isApproved, false),
      className: item.className || '',
      imageUrl: item.imageUrl || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this testimonial?')) return;
    try { await api.delete(`/testimonials/${id}`); alert('✅ Testimonial deleted!'); fetchTestimonials(); }
    catch (error) { console.error('Delete error:', error); alert('❌ Error deleting testimonial'); }
  };

  const handleApprove = async (item) => {
    try {
      const approved = toBool(item.isApproved, false);
      const next = !approved;
      await api.put(`/testimonials/${item.id}`, {
        name: item.name,
        role: item.role || 'Parent',
        className: item.className || '',
        testimonial: item.testimonial,
        imageUrl: item.imageUrl || '',
        rating: item.rating || 5,
        isActive: toBool(item.isActive, true),
        isApproved: next,
        featured: toBool(item.featured, false),
      });
      alert(`✅ Testimonial ${next ? 'approved' : 'unapproved'}!`);
      fetchTestimonials();
    } catch (error) {
      console.error('Error updating approval:', error);
      alert('❌ Could not update approval');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', role: 'Parent', testimonial: '', rating: 5, featured: false, isApproved: true, className: '', imageUrl: '' });
  };

  if (loading) return <AdminLayout><div className="p-6">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-blue-900">Testimonials Management</h1>
          <button onClick={() => setShowForm(true)}
            className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition flex items-center gap-2">
            <FaPlus /> Add Testimonial
          </button>
        </motion.div>

        {showForm && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">{editingId ? 'Edit' : 'Add'} Testimonial</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="Full Name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                    <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      <option value="Parent">Parent</option>
                      <option value="Student">Student</option>
                      <option value="Alumni">Alumni</option>
                      <option value="Teacher">Teacher</option>
                    </select>
                  </div>
                </div>
                {formData.role === 'Student' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Class</label>
                    <input type="text" value={formData.className} onChange={(e) => setFormData({ ...formData, className: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., Class 10" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Image URL (optional)</label>
                  <input type="url" value={formData.imageUrl} onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Testimonial *</label>
                  <textarea value={formData.testimonial} onChange={(e) => setFormData({ ...formData, testimonial: e.target.value })} required rows="4"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="Write testimonial..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button key={star} type="button" onClick={() => setFormData({ ...formData, rating: star })}
                        className={`text-2xl ${star <= formData.rating ? 'text-yellow-400' : 'text-gray-300'}`}>
                        <FaStar />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900" />
                    <label className="text-sm text-gray-700">Featured</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.isApproved} onChange={(e) => setFormData({ ...formData, isApproved: e.target.checked })}
                      className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900" />
                    <label className="text-sm text-gray-700">Approved</label>
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="flex-1 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition font-semibold">
                    {editingId ? 'Update' : 'Add'} Testimonial
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition font-semibold">Cancel</button>
                </div>
              </form>
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {testimonials.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-lg text-blue-900">{item.name}</h3>
                    <span className="px-2 py-1 bg-gray-100 rounded text-sm">{item.role}</span>
                    {item.className && (<span className="text-sm text-gray-500">{item.className}</span>)}
                  </div>
                  <div className="flex mb-2">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className={i < (item.rating || 0) ? 'text-yellow-400' : 'text-gray-300'} />
                    ))}
                  </div>
                  <p className="text-gray-600 italic">"{item.testimonial}"</p>
                  <div className="mt-3 flex gap-3">
                    {item.featured && (<span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Featured</span>)}
                    <span className={`px-2 py-1 rounded text-xs ${item.isApproved ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                      {item.isApproved ? 'Approved' : 'Pending Approval'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button type="button" onClick={() => handleApprove(item)}
                    className={`p-2 rounded ${item.isApproved ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`} title={item.isApproved ? 'Unapprove' : 'Approve'}>
                    {item.isApproved ? <FaCheck size={18} /> : <FaTimes size={18} />}
                  </button>
                  <button onClick={() => handleEdit(item)} className="text-blue-900 hover:text-blue-700"><FaEdit size={18} /></button>
                  <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800"><FaTrash size={18} /></button>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </AdminLayout>
  );
}

export default TestimonialsManagement;
